import { TRefElementInstance } from 'widgets/toolbox/RefElement';

const ALPHA_NUM_PATTERN = /[^a-zA-Z0-9]/g;

/**
 * @description Base InputCheckbox implementation
 * @param BasicInput Base widget for extending
 * @returns Input Checkbox class
 */
function InputFileClassCreator(BasicInput: ReturnType<typeof import('widgets/forms/BasicInput').default>) {
    /**
     * @category widgets
     * @subcategory forms
     * @class InputFile
     * @augments BasicInput
     * @classdesc File input implementation. Represents input `File` element together with widget-related HTML markup.
     * HTML structure assembled on backend and injected into resulted html markup by using `formElement` component
     * and dynamic forms configuration JSON.
     * @property {string} data-widget - Widget name `inputFile`
     * @example <caption>InputFile definition in dynamicForms.js</caption>
     * ...
     * // fields -> input -> inputFiles
     * inputFiles: {
     *     'label.after': true,
     *     'element.type': 'file'
     * }
     * ...
     * @example <caption>Insertion of InputFile inside ISML templates</caption>
     * <isset name="formElement" value="${require('forms/formElement')}" scope="page"/>
     * ...
     * <form>
     *     ...
     *     <isprint value="${
     *      formElement(pdict.contactForm.inputFiles).render('')
     *  }" encoding="off"/>
     *     ...
     * </form>
     * @example <caption>Resulted HTML structure for InputFile</caption>
     * <div class="b-form_field"
     *      data-widget="inputFile"
     *      data-event-change="onChange"
     *     data-event-blur="handleBlur"
     *     data-event-focus=""
     *     data-file-size-mb="3" data-file-quantity="3"
     *     data-file-allowed-extensions="pdf,jpeg,jpg,png"
     *     data-file-name-characters="120" data-id="dwfrm_contactUs_inputFiles"
     *     data-validation-config='{"patterns":{},"
     *          errors":{
     *              "quantity":"Please limit attachments to 3 files per message.",
     *               "size":"One or more files are above our size limit of 3MB.",
     *               "extension":"Please upload files in PDF, JPEG, JPG or PNG formats only.",
     *              "filename":"Please limit each file name to 120 characters."
     *          },
     *          "mandatory":"false"}'
     *     data-initialized="1">
     *     <label class="b-form_field-label m-file_upload" for="dwfrm_contactUs_inputFiles" data-ref="label">
     *         Attachment
     *          <span class="b-form_field-required_mark" data-ref="requiredMark" hidden="hidden">
     *         </span>
     *      </label>
     *      <input type="file" class="b-input m-input_file" id="dwfrm_contactUs_inputFiles" placeholder="" aria-describedby="dwfrm_contactUs_inputFiles-error" name="dwfrm_contactUs_inputFiles" value="" data-ref="field" aria-invalid="false">
     *      <div class="b-form_field-message" role="alert" data-ref="errorFeedback" id="dwfrm_contactUs_inputFiles-error" hidden="hidden">One or more files are above our size limit of 3MB.</div>
     *  </div>
     */
    class InputFile extends BasicInput {
        fieldHTMLInputElement: HTMLInputElement | null = null;

        fieldRefElement: TRefElementInstance | null = null;

        valid: boolean = true;

        ERROR_TYPES = {
            SIZE: 'size',
            QUANTITY: 'quantity',
            FILENAME: 'filename',
            EXTENSION: 'extension'
        };

        prefs() {
            return {
                fileSizeMb: 0,
                fileQuantity: 1,
                fileAllowedExtensions: '',
                fileNameCharacters: '',
                ...super.prefs()
            };
        }

        /**
         * Initializes DOM references used by the widget.
         * Caches the underlying `HTMLInputElement` and its RefElement wrapper.
         *
         */
        init() {
            this.fieldHTMLInputElement = this.ref('field').get(0) as HTMLInputElement;
            this.fieldRefElement = this.ref('field');
        }

        /**
         * Handles input change events: clears error state and marks the widget as valid.
         *
         */
        onChange() {
            if (this.validateFileQuantity()) {
                this.fieldRefElement?.removeClass(this.prefs().classesErrorState).attr('aria-invalid', 'false');
                this.ref('self').removeClass(this.prefs().classesWrapperErrorState);
                this.ref('errorFeedback').hide();
                this.toggleValid(true);
                this.updateFilesList();
            }
        }

        /**
         * Handles files being dropped by users.
         *
         * @param el - Input file ref element
         * @param e event 'drop' containing FileList of dropped files.
         */
        onDrop(el, e) {
            if (e.dataTransfer?.files) {
                e.preventDefault();
                const dt = this.getFilesAsDataTransfer();
                const droppedFiles: Array<File> = Array.from(e.dataTransfer.files);
                const fileQuantity = this.prefs().fileQuantity;

                droppedFiles.forEach(f => {
                    if (dt.files.length < fileQuantity) {
                        dt.items.add(f);
                    }
                });

                this.resetInputFiles(dt);

                this.onChange();
            }
        }

        /**
         * Prevents default behavior of onDragOver event
         *
         * @param el - Input RefElement
         * @param e - event 'dragover'
         */
        onDragOver(el, e) {
            e.preventDefault();
        }

        /**
         * Updates visual list with uploaded files.
         */
        updateFilesList() {
            const files = this.getFiles();
            const list = this.ref('fileList').get();

            if (list) {
                list.innerHTML = '';

                files.forEach((file, index) => {
                    const item = document.createElement('div');

                    if (item) {
                        item.className = 'b-upload_file';

                        const nameSpan = document.createElement('span');
                        const removeBtn = document.createElement('button');

                        nameSpan.textContent = file.name;
                        removeBtn.textContent = 'x';
                        removeBtn.className = 'b-upload_remove';
                        removeBtn.type = 'button';
                        removeBtn.setAttribute('aria-label', `Remove ${file.name}`);
                        removeBtn.dataset.index = String(index);

                        removeBtn?.addEventListener('click', () => {
                            this.removeFile(index);
                        });

                        item.appendChild(nameSpan);
                        item.appendChild(removeBtn);
                        list.appendChild(item);
                    }
                });
            }
        }

        /**
         * Removes file from upload list after user clicks remove button
         *
         * @param index - index of file to be removed.
         */
        removeFile(index) {
            const dt = this.getFilesAsDataTransfer([index]);

            this.resetInputFiles(dt);
        }

        /**
         * Returns a new Data Transfer object from the file input elements' current
         * files. Accepts an array of indexes to skip.
         *
         * @param skipIndexes - (Optional) Array of indexes to skip on Data Transfer instance
         * @returns Data Transfer instance
         */
        getFilesAsDataTransfer(skipIndexes?) {
            const dt = new DataTransfer();
            const files = this.getFiles();

            files.forEach((file, i) => {
                if (!skipIndexes || skipIndexes?.indexOf(i) < 0) {
                    dt.items.add(file);
                }
            });

            return dt;
        }

        /**
         * Resets input file element's file list to whatever is in the Data Transfer
         * object's files.
         *
         * @param dt - Data Transfer object
         */
        resetInputFiles(dt: DataTransfer) {
            if (this.fieldHTMLInputElement) {
                this.fieldHTMLInputElement.files = dt.files;
                this.onChange();
            }
        }

        /**
         * Validates current widget state.
         */
        validate(): boolean {
            if (!this.shown || this.disabled || this.prefs().skipValidation) {
                return true;
            }

            return this.valid;
        }

        /**
         * Sets the internal `valid` flag used by `validate()`.
         *
         * @param val New validity value.
         */
        toggleValid(val) {
            this.valid = val;
        }

        /**
         * Updates native constraint validation state and widget error UI for a given error type.
         *
         * @param error (optional)
         *
         * Error key matching `prefs().validationConfig.errors` (i.e. "size", "quantity", "filename", "extension").
         */
        updateCustomValidityState(error?: string): void {
            const validationConfig = this.prefs().validationConfig;
            const errorMsg = error ? validationConfig.errors[error] : '';

            if (typeof error === 'string' && this.fieldHTMLInputElement instanceof HTMLInputElement) {
                this.fieldHTMLInputElement.setCustomValidity(errorMsg);
                this.setError(errorMsg);
                this.toggleValid(false);
            }
        }

        /**
         * Builds the payload objects for all selected files.
         * Performs quantity and total size validation before parsing.
         * When invalid, sets validity state and resolves to an empty array.
         *
         * @returns Promise resolving to an array of parsed file payload objects.
         */
        getFilesPayload(): Promise<Array<object>> {
            const files = this.getFiles();
            const isValidQty = this.validateFileQuantity();

            if (!isValidQty || !this.validateTotalSize()) {
                const errorType = (!isValidQty && this.ERROR_TYPES.QUANTITY) || this.ERROR_TYPES.SIZE;

                this.updateCustomValidityState(errorType);

                return Promise.resolve([]);
            }

            const payloadFiles = Promise.all(files.map(this.getValidParsedFiles.bind(this)));

            return payloadFiles;
        }

        /**
         * Validates and converts a single File into a payload object:
         * - validates size
         * - converts to base64
         * - validates detected file type from the base64 payload
         * - returns normalized payload fields
         *
         * @param file The browser File object.
         * @returns Parsed file payload.
         */
        async getValidParsedFiles(file) {
            const sanitizedFileName = this.getSanitizedFileName(file);
            const isFileValid = this.validateFileSize(file) && this.validateFileType(file) && !!sanitizedFileName;

            if (!isFileValid) {
                throw new Error('Invalid file.');
            }

            const base64Payload = await this.toBase64(file);

            return {
                fileName: sanitizedFileName,
                type: file.type,
                size: file.size,
                content: base64Payload
            };
        }

        /**
         * Converts a File into a base64 payload string (without the data URL prefix).
         *
         * @param file The browser File object.
         * @returns Promise resolving to the base64 data (no "data:*;base64," prefix).
         */
        toBase64(file: File) {
            return new Promise((resolve, reject) => {
                const fileReader = new FileReader();

                fileReader.onload = () => {
                    const filePaylod = fileReader.result;

                    if (typeof filePaylod === 'string') {
                        const base64Payload = filePaylod.split(',')[1] || '';

                        return resolve(base64Payload);
                    }

                    return reject(new Error('Unable to process base64 files.'));
                };

                fileReader.readAsDataURL(file);
            });
        }

        /**
         * Returns a sanitized file name:
         * - validates maximum file name length
         * - validates and extracts extension
         * - strips non-alphanumeric characters from the base name
         *
         * Note: if extension is invalid, this currently calls `updateCustomValidityState` and returns its result.
         *
         * @param file The browser File object.
         * @returns Sanitized file name, or empty string if invalid extension, triggering error handling.
         */
        getSanitizedFileName(file) {
            const fileName = file.name;

            if (!this.validateFileNameLength(fileName)) {
                return '';
            }

            const fileExt = this.getFileExtension(file);

            if (!fileExt) {
                return '';
            }

            const fileNameNoExt = this.getFileNameNoExt(file);
            const sanitizedName = fileNameNoExt.replaceAll(ALPHA_NUM_PATTERN, '').concat('.' + fileExt);

            return sanitizedName;
        }

        /**
         * Returns the file name without extension (sanitized), based on the last "." position.
         *
         * @param file The browser File object.
         * @returns Sanitized file name without extension, or `false` when no extension is present.
         */
        getFileNameNoExt(file) {
            const dotIdx = file.name.lastIndexOf('.');

            return dotIdx !== -1 && file.name.slice(0, dotIdx).replaceAll(ALPHA_NUM_PATTERN, '');
        }

        /**
         * Extracts and validates the file extension against allowed extensions.
         *
         * @param file The browser File object.
         * @returns Lowercased extension (without ".") if allowed, otherwise empty string.
         */
        getFileExtension(file) {
            let dotIdx = file?.name.toLowerCase().lastIndexOf('.');

            if (dotIdx !== -1) {
                dotIdx++;
                const ext = file.name.slice(dotIdx);

                if (this.prefs().fileAllowedExtensions.includes(ext)) {
                    return ext;
                }
            }

            this.updateCustomValidityState(this.ERROR_TYPES.EXTENSION);

            return '';
        }

        /**
         * Generates an Array from the FileList of the input element
         *
         * @returns Array of files
         */
        getFiles() {
            return Array.from(this.fieldHTMLInputElement?.files ?? []);
        }

        /**
         * Validates file name's character length is within boundaries.
         *
         * @param fileName Name of file
         *
         * @returns whether filename length is valid
         */
        validateFileNameLength(fileName) {
            const isValidFileName = fileName.length <= this.prefs().fileNameCharacters;

            if (!isValidFileName) {
                this.updateCustomValidityState(this.ERROR_TYPES.FILENAME);
            }

            return isValidFileName;
        }

        /**
         * Validates a single file size against the per-file limit (`prefs().fileSizeMb`).
         * If invalid, sets the widget error state.
         *
         * @param file The browser File object.
         * @returns whether a file's size is within boundaries (3MB)
         */
        validateFileSize(file) {
            const isValidFileSize = file.size <= this.prefs().fileSizeMb * 1024 * 1024;

            if (!isValidFileSize) {
                this.updateCustomValidityState(this.ERROR_TYPES.SIZE);
            }

            return isValidFileSize;
        }

        /**
         * Validates the detected file type (based on base64 magic bytes) against allowed extensions.
         * If invalid, sets the widget error state.
         *
         * @param file Base64 file payload (no data URL prefix).
         */
        validateFileType(file) {
            const MIME_BY_EXT = ['image/jpeg', 'application/pdf', 'image/png'];

            const fileType = file.type || '';
            const isValidMime = MIME_BY_EXT.includes(fileType);

            if (!isValidMime) {
                this.updateCustomValidityState(this.ERROR_TYPES.EXTENSION);
            }

            return isValidMime;
        }

        /**
         * Validates selected file quantity against `prefs().fileQuantity`.
         * If invalid, sets the widget error state.
         *
         * @returns True when within the allowed limit; otherwise false.
         */
        validateFileQuantity(): boolean {
            const isValidQty = (this.fieldHTMLInputElement?.files?.length ?? 0) <= this.prefs().fileQuantity;

            if (!isValidQty) {
                this.updateCustomValidityState(this.ERROR_TYPES.QUANTITY);
            }

            return isValidQty;
        }

        /**
         * Validates total size of selected files against the configured limits.
         * Computes total bytes and converts to MB; compares to `files.length * prefs().fileSizeMb`.
         * If invalid, sets the widget error state.
         *
         * @returns True when total size is within allowed limit; otherwise false.
         */
        validateTotalSize(): boolean {
            const files = this.getFiles();
            const totalSizeMb =
                files.reduce((total, file) => {
                    return total + file.size;
                }, 0) /
                (1024 * 1024);

            const isValidSize = totalSizeMb <= files.length * this.prefs().fileSizeMb;

            if (!isValidSize) {
                this.updateCustomValidityState(this.ERROR_TYPES.SIZE);
            }

            return isValidSize;
        }
    }

    return InputFile;
}

export type TInputFile = ReturnType<typeof InputFileClassCreator>;

export type TInputFileInstance = InstanceType<TInputFile>;

export default InputFileClassCreator;
